import {
  MarketingTemporalPhasePicker,
  type TemporalPhaseTag,
} from "@/react/components/marketing/temporal";
import type { SurfaceSolutionTag } from "../api/types";
import { getAcceptReadiness } from "../utils/mergeTagIds";
import PromoteSurfaceTagPicker from "./PromoteSurfaceTagPicker";

type Props = {
  tags: SurfaceSolutionTag[];
  temporalTags: TemporalPhaseTag[];
  solutionTagIds: number[];
  onSolutionTagIdsChange: (ids: number[]) => void;
  temporalTagId: number | null;
  onTemporalTagIdChange: (id: number | null) => void;
  disabled?: boolean;
  /** Inline amber validation under the tag picker (bulk sheet). Overlay uses footer hints instead. */
  showInlineHints?: boolean;
};

/**
 * Shared temporal phase + solution tag fields for single-asset overlay and bulk accept.
 */
export default function PromoteSurfaceAcceptFields({
  tags,
  temporalTags,
  solutionTagIds,
  onSolutionTagIdsChange,
  temporalTagId,
  onTemporalTagIdChange,
  disabled,
  showInlineHints = false,
}: Props) {
  const { needsSolutionTag, needsTemporalTag } = getAcceptReadiness({
    solutionTagIds,
    temporalTagId,
    temporalTagCount: temporalTags.length,
  });

  return (
    <>
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Temporal phase
        </div>
        <MarketingTemporalPhasePicker
          tags={temporalTags}
          value={temporalTagId}
          onChange={onTemporalTagIdChange}
          disabled={Boolean(disabled)}
        />
      </div>

      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Solution tags
          {solutionTagIds.length > 0 && (
            <span className="ml-1.5 font-medium normal-case tracking-normal text-foreground">
              · {solutionTagIds.length} selected
            </span>
          )}
        </div>
        <PromoteSurfaceTagPicker
          tags={tags}
          value={solutionTagIds}
          onChange={onSolutionTagIdsChange}
        />
        {showInlineHints && needsSolutionTag && (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Select at least one solution tag to continue.
          </p>
        )}
        {showInlineHints && needsTemporalTag && (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Select a temporal phase to continue.
          </p>
        )}
      </div>
    </>
  );
}
